// @flow
import 'isomorphic-fetch'
import { API, createDefaultHeaders } from '../../util/api'
import {
  createControllerMoment,
  getProtoDateObjectFromMomentInstance
} from '../../util/time'
import { del as deleteDevice } from './crud'
import { deviceFromGens } from '../../util/deviceFromGens'
import { isInvalid } from '@rachio-npm/com-rachio-web-core/util/validation'
import { isShareCode } from '../../util/location'
import { logger } from '../../util/logger'
import { updateZoneSchedules } from '../zone/index'
import * as Creators from './index'
import * as scheduleActionCreators from '../schedule'
import * as scheduleSchema from '../schedule/schemas'
import * as Schema from './schemas'
import * as zoneActionCreators from '../zone'
import changeCase from 'change-object-case'

// types
import type { DateObject, DateOrTimeObject } from '../../util/time'
import type { Dispatch } from 'redux'
import type { LightBarOptions } from '../../components/LightBarSettings'
import type { LinkedSensorType, DeviceWateringSummary } from './types'
import type { ThunkPromise } from '../../util/redux'
import typeof { ValidationError } from '@rachio-npm/com-rachio-web-core/util/validation'

/**
 * Additional methods around device functionality
 * @module ducks/device/methods
 */

// SERIAL # ///////////////////////////////////////////////////////////////////

/**
 * Find Device by serial number
 * @param {string} authToken - User Auth Token
 * @param {string} serialNumber - Device serial number
 * @param {string} [userId] - Optional user id
 * @returns {function} - Dispatch function
 */
export const getDeviceBySerialNumber = (
  authToken: string,
  serialNumber: string
) => {
  return function(dispatch: Dispatch<Object>): Promise<*> {
    const Api = new API()
    const rawReqData: Object = { deviceSerial: serialNumber }
    const reqData = changeCase.snakeKeys(rawReqData, { recursive: true })
    const error = isInvalid(Schema.AssignProtoDeviceRequestSchema, reqData)
    const headers = {
      ...createDefaultHeaders(authToken),
      'Content-Type': 'application/json'
    }

    dispatch(Creators.getBySerialNumberInit())

    if (error) {
      return Promise.reject(dispatch(Creators.getBySerialNumberComplete(error)))
    } else {
      // Return API call
      return Api.call('device/getNascentIrrigationController/' + serialNumber, {
        headers: headers
      })
        .then(response =>
          response.json().then(json => {
            return dispatch(Creators.getBySerialNumberComplete(json))
          })
        )
        .catch(err => dispatch(Creators.getBySerialNumberComplete(err)))
    }
  }
}

// ZONES //////////////////////////////////////////////////////////////////////

/**
 * Get `getZones` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @return {object} - Config object
 */
const getZonesModes = (authToken: string, deviceId: string) => {
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/listZonesQR/' + deviceId + '/' + authToken
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/listZones/' + deviceId
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Get device zones
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @returns {function} - Dispatch function
 */
export const getZones = (authToken: string, deviceId: string) => {
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    const mode = getZonesModes(authToken, deviceId)
    const Api = new API()

    dispatch(Creators.getZonesInit({ deviceId }))

    // Return API call
    return Api.call(mode.url, {
      method: 'GET',
      headers: mode.headers
    })
      .then(response => response.json())
      .then(json => {
        const zoneSummary = json.zoneSummary
        const zoneIds = zoneSummary.map(zone => zone.zoneDetail.id)

        dispatch(
          zoneActionCreators.set(zoneSummary.map(zone => zone.zoneDetail))
        )
        dispatch(
          zoneActionCreators.setState(
            zoneSummary.map(zone => ({
              id: zone.zoneDetail.id,
              META: { state: zone.zoneState }
            }))
          )
        )

        dispatch(Creators.setZoneIds({ deviceId: deviceId, zoneIds: zoneIds }))
        return dispatch(
          Creators.getZonesComplete({ deviceId: deviceId, zoneIds: zoneIds })
        )
      })
      .catch(err => {
        err.payload = err.payload || {}
        err.payload.deviceId = deviceId
        dispatch(Creators.getZonesComplete(err))
      })
  }
}

// SCHEDULES //////////////////////////////////////////////////////////////////

export const getSchedules = (
  authToken: string,
  deviceId: string,
  zoneId?: string
) => {
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    const Api = new API()
    const idObject = zoneId
      ? { zoneId: { id: [zoneId] } }
      : { deviceId: { id: [deviceId] } }
    const reqData = changeCase.snakeKeys(idObject, { recursive: true })
    const headers = {
      ...createDefaultHeaders(authToken),
      'Content-Type': 'application/json'
    }
    const error = isInvalid(Schema.GetSchedulesRequest, reqData)

    dispatch(Creators.getSchedulesInit({ deviceId, zoneId }))

    if (error) {
      // $FlowFixMe
      return Promise.reject(dispatch(Creators.getSchedulesComplete(error)))
    } else {
      // Return API call
      return Api.call('schedule/getSchedules', {
        body: JSON.stringify(reqData),
        method: 'POST',
        headers: headers
      })
        .then(response => response.json())
        .then(json => {
          const scheduleIds = json.schedule.map(schedule => schedule.id)
          dispatch(scheduleActionCreators.set(json.schedule))
          dispatch(Creators.setScheduleIds({ deviceId, scheduleIds }))
          dispatch(updateZoneSchedules(json.schedule))
          return dispatch(
            Creators.getSchedulesComplete({ deviceId, scheduleIds })
          )
        })
        .catch(err => {
          err.deviceId = deviceId
          dispatch(Creators.getSchedulesComplete(err))
        })
    }
  }
}

// STATE //////////////////////////////////////////////////////////////////////

/**
 * Get `getState` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @return {object} - Config object
 */
const getStateModes = (authToken: string, deviceId: string) => {
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/getDeviceStateQR/' + deviceId + '/' + authToken
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/getDeviceState/' + deviceId
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Get device state
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @returns {function} - Dispatch function
 */
export const getState = (authToken: string, deviceId: string) => {
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    const Api = new API()
    const mode = getStateModes(authToken, deviceId)
    const headers = mode.headers

    dispatch(Creators.getStateInit({ deviceId }))

    // Return API call
    return Api.call(mode.url, {
      method: 'GET',
      headers: headers
    })
      .then(response => response.json())
      .then(json =>
        dispatch(
          Creators.getStateComplete({ ...json.state, deviceId: deviceId })
        )
      )
      .catch(err => dispatch(Creators.getStateComplete({ ...err, deviceId })))
  }
}

// WATERING ///////////////////////////////////////////////////////////////////

/**
 * Get `stopWatering` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @return {object} - Config object
 */
const stopWateringModes = (authToken: string, deviceId: string) => {
  const reqData = changeCase.snakeKeys({ deviceId }, { recursive: true })
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/stopWateringQR/',
      reqData: { ...reqData, code: authToken }
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/stopWatering',
      reqData: reqData
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Stop any current waterings
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @returns {function} - Dispatch function
 */
export const stopWatering = (authToken: string, deviceId: string) => {
  return function() {
    const Api = new API()
    const mode = stopWateringModes(authToken, deviceId)
    const headers = mode.headers

    // Return API call
    return Api.call(mode.url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(mode.reqData)
    }).then(response => response.json())
  }
}

/**
 * Get `pauseWatering` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @param  {number} secondsPaused - Number of second to pause for
 * @return {object} - Config object
 */
const pauseWateringModes = (
  authToken: string,
  deviceId: string,
  secondsPaused: number
) => {
  const reqData = changeCase.snakeKeys(
    { deviceId, secondsPaused },
    { recursive: true }
  )
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/pauseZoneRunQR',
      reqData: { ...reqData, code: authToken }
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/pauseZoneRun',
      reqData: reqData
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Pause any current waterings
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @param {number} secondsPaused - Number of seconds to pause for
 * @returns {function} - Dispatch function
 */
export const pauseWatering = (
  authToken: string,
  deviceId: string,
  secondsPaused: number
) => {
  return function() {
    const Api = new API()
    const mode = pauseWateringModes(authToken, deviceId, secondsPaused)
    const headers = mode.headers

    // Return API call
    return Api.call(mode.url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(mode.reqData)
    }).then(response => response.json())
  }
}

/**
 * Get `resumeWatering` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @return {object} - Config object
 */
const resumeWateringModes = (authToken: string, deviceId: string) => {
  const reqData = changeCase.snakeKeys({ deviceId }, { recursive: true })
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/resumeZoneRunQR',
      reqData: { ...reqData, code: authToken }
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/resumeZoneRun',
      reqData: reqData
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Resume any current waterings
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @returns {promise} - Api call
 */
export const resumeWatering = (authToken: string, deviceId: string) => {
  return function(): Promise<*> {
    const Api = new API()
    const mode = resumeWateringModes(authToken, deviceId)

    const error = isInvalid(
      scheduleSchema.ResumeZoneRunRequestSchema,
      mode.reqData
    )

    if (error) {
      logger.error(error)
      return Promise.reject({ error: true, payload: error })
    }

    // Return API call
    return Api.call(mode.url, {
      method: 'POST',
      headers: mode.headers,
      body: JSON.stringify(mode.reqData)
    }).then(response => response.json())
  }
}

/**
 * Get `extendWatering` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @param  {number} secondsExtended - secondsExtended
 * @return {object} - Config object
 */
const extendWateringModes = (
  authToken: string,
  deviceId: string,
  secondsExtended: number
) => {
  const reqObject = { deviceId, secondsExtended }
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/extendZoneRunQR',
      reqData: changeCase.snakeKeys(
        { ...reqObject, code: authToken },
        { recursive: true, arrayRecursive: true }
      ),
      reqObject
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/extendZoneRun',
      reqData: changeCase.snakeKeys(reqObject, {
        recursive: true,
        arrayRecursive: true
      }),
      reqObject
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Extend any current waterings
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @param {number} secondsExtended - Number of seconds to pause for
 * @returns {function} - Dispatch function
 */
export const extendWatering = (
  authToken: string,
  deviceId: string,
  secondsExtended: number
) => {
  return function() {
    const Api = new API()
    const mode = extendWateringModes(authToken, deviceId, secondsExtended)
    const headers = {
      ...createDefaultHeaders(authToken),
      'Content-Type': 'application/json'
    }

    // Return API call
    return Api.call(mode.url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(mode.reqData)
    }).then(response => response.json())
  }
}

// DETAILS ////////////////////////////////////////////////////////////////////

/**
 * Get `getDeviceDetails` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @return {object} - Config object
 */
const getDeviceDetailsModes = (authToken: string, deviceId: string) => {
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: `device/getDeviceDetailsQR/${deviceId}/${authToken}`,
      reqData: { code: authToken }
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: `device/getDeviceDetails/${deviceId}`,
      reqData: null
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Get device details
 * @param {string} userAuthToken - User's authorization token
 * @param {string} deviceId - Device id
 * @returns {promise} The action produced by `getDetailsComplete()`
 */
export const getDeviceDetails = (userAuthToken: string, deviceId: string) => {
  const Api = new API()
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    const mode = getDeviceDetailsModes(userAuthToken, deviceId)
    const reqObj = {
      method: 'GET',
      headers: mode.headers
    }

    dispatch(Creators.getDetailsInit(deviceId))

    return Api.call(mode.url, reqObj)
      .then(response => response.json())
      .then(json => {
        const device =
          json.gen1IrrigationController ||
          json.gen2IrrigationController ||
          json.gen3IrrigationController ||
          json.linkedSensor ||
          json.virtualWeatherStation ||
          json.weatherStation ||
          json.wirelessFlowSensor
        dispatch(Creators.setDetails(device))
        return dispatch(Creators.getDetailsComplete(json))
      })
      .catch(err => dispatch(Creators.getDetailsComplete(err)))
  }
}

export const getNetworkState = (token: string, deviceId: string) => {
  const Api = new API()

  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    const reqObj = {
      method: 'GET',
      headers: {
        ...createDefaultHeaders(token),
        'Content-Type': 'application/json'
      }
    }

    dispatch(Creators.getNetworkStateInit(deviceId))

    return Api.call(`device/getNetworkState/${deviceId}`, reqObj)
      .then(res => res.json())
      .then(networkState => {
        dispatch(
          Creators.updateMeta({
            deviceId,
            data: {
              networkState
            }
          })
        )

        return dispatch(
          Creators.getNetworkStateComplete({ deviceId, networkState })
        )
      })
      .catch(error =>
        dispatch(Creators.getNetworkStateComplete({ deviceId, error }))
      )
  }
}

/**
 * Update device details
 * @param {string} userAuthToken - User's authorization token
 * @param {object} updateData - Data to update
 * @returns {promise} An action related to the device retrieval process
 */
export const updateDetails = (userAuthToken: ?string, updateData: Object) => {
  const Api = new API()
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    const reqData = changeCase.snakeKeys(updateData, { recursive: true })
    const error = isInvalid(Schema.UpdateIrrigationControllerRequest, reqData)
    const reqObj = {
      method: 'PUT',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqData)
    }

    dispatch(Creators.updateDetailsInit(updateData))
    if (error) {
      // $FlowFixMe
      return Promise.reject(dispatch(Creators.updateDetailsComplete(error)))
    } else {
      return Api.call('device/updateIrrigationController', reqObj)
        .then(response => response.json())
        .then(json => {
          const device = deviceFromGens(json)
          dispatch(Creators.setDetails(device))
          return dispatch(Creators.updateDetailsComplete(json))
        })
        .catch(err => dispatch(Creators.updateDetailsComplete(err)))
    }
  }
}

// LIGHTBAR //////////////////////////////////////////////////////////////////////

type UpdateLightBarData = {
  lightBarSetting: LightBarOptions // string light_bar_setting = 1;
}

/**
 * Update lightbar setting
 * @param {string} userAuthToken - User's authorization token
 * @param {UpdateLightBarData} updateData - Data to update
 * @returns {function} - Api call
 */
export const setLightBarSetting = (
  userAuthToken: ?string,
  updateData: UpdateLightBarData
) => {
  const Api = new API()
  return function() {
    const reqData = changeCase.snakeKeys(updateData, { recursive: true })
    const error = isInvalid(Schema.SetLightBarSettingRequest, reqData)
    const reqObj = {
      method: 'PUT',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqData)
    }
    if (!error) {
      return Api.call('device/setLightBarSetting', reqObj).then(resp =>
        resp.json()
      )
    }
  }
}

// SENSORS ////////////////////////////////////////////////////////////////////

type UpdateLinkedSensorData = {
  id: string, // string id = 1;
  make?: string, // google.protobuf.StringValue make = 2;
  model?: string, //google.protobuf.StringValue model = 3;
  kfactor?: number, // google.protobuf.DoubleValue kfactor = 4;
  offset?: number, // google.protobuf.DoubleValue offset = 5;
  enabled?: boolean, // bool enabled = 6;
  sensorType?: LinkedSensorType, // LinkedSensorType sensorType = 7;
  linkedControllerId: string // string linked_controller_id = 8;
}

/**
 * Update Sensor NOTE: some of the casing stuff is due to the backend using an odd casing
 * @param  {string} userAuthToken - User auth token
 * @param  {object} updateData - Update data
 * @return {promise} - Result of call
 */
export const updateSensor = (
  userAuthToken: string,
  updateData: UpdateLinkedSensorData
) => {
  const Api = new API()
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    // const reqData = changeCase.snakeKeys(updateData)
    const reqData = updateData
    const reqObj = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqData)
    }

    // $FlowFixMe
    dispatch(Creators.createRainSensorInit(reqData))

    return Api.call('device/updateLinkedSensor', reqObj)
      .then(response => response.json())
      .then(json => {
        const rainSensor = json.value
        dispatch(Creators.setDetails(rainSensor))
        return dispatch(Creators.createRainSensorComplete(json))
      })
      .catch(err => dispatch(Creators.createRainSensorComplete(err)))
  }
}

/**
 * Get list of available flow sensors. NOTE: This does *not* dispatch any actions
 * @param  {string} userAuthToken - User auth token
 * @return {promise} - Array of flow sensor options
 */
export const getFlowSensorOptions = (userAuthToken: string) => {
  return function() {
    const Api = new API()
    const headers = {
      ...createDefaultHeaders(userAuthToken),
      'Content-Type': 'application/json'
    }
    return Api.call('device/getFlowSensorData', { headers }).then(resp =>
      resp.json()
    )
  }
}

/**
 * Delete a wireless sensor.
 * @param {string} userAuthToken - User's auth token
 * @param {string} sensorId - wireless sensor id
 * @param {string} controllerId - connected controller id
 * @return {promise} - removeWirelessSensorComplete action
 */
export const removeWirelessSensor = (
  userAuthToken: string,
  sensorId: string,
  controllerId: string
) => {
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    dispatch(Creators.removeWirelessSensorInit(sensorId))

    return dispatch(deleteDevice(userAuthToken, sensorId)).then(res => {
      if (res.error) {
        return dispatch(Creators.removeWirelessSensorComplete(res))
      } else {
        // Refresh the connected controller
        return dispatch(getDeviceDetails(userAuthToken, controllerId)).then(
          () => Creators.removeWirelessSensorComplete(res)
        )
      }
    })
  }
}

// WATERING USAGE //////////////////////////////////////////////////////////
type GetWateringSummaryParams = {
  token: string,
  deviceId: string,
  timeZone: string,
  offset?: number,
  startDate?: DateOrTimeObject | string,
  noStoreUpdate?: boolean
}

export type GetWateringSummaryObj = {
  deviceId: string,
  offset: number,
  startDate: DateObject,
  endDate: DateObject,
  wateringSummary: DeviceWateringSummary
}
/**
 * get watering usage data for all types; used for watering usage graph
 * start and end dates need to be converted from 0 to 1 based month index
 * @param {string} token user's auth token
 * @param {string} deviceId device Id
 * @param {string} timeZone - time zone string
 * @param {number} offset offset in months from current month, going back, including this means only one month of data is returned
 * @param {string} startDate - start date from which to return all data until present
 * @return {promise} resolving to the dispatch function
 */
export function getWateringSummary({
  token = '',
  deviceId,
  startDate: startDateProp,
  timeZone,
  offset = 0,
  noStoreUpdate
}: GetWateringSummaryParams) {
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch): ThunkPromise<GetWateringSummaryObj> {
    const Api = new API()

    const endMoment =
      offset === 0
        ? createControllerMoment(timeZone)
        : // get 1 month of usage history
          // trying to make as few moment calls as possible for performance
          createControllerMoment(timeZone)
            .subtract(offset, 'months')
            .endOf('month')

    const startMoment = startDateProp
      ? createControllerMoment(timeZone, startDateProp)
      : endMoment.clone().startOf('month')

    const startDate = getProtoDateObjectFromMomentInstance(startMoment)
    const endDate = getProtoDateObjectFromMomentInstance(endMoment)

    const requestData = {
      deviceId,
      startDate,
      endDate
    }

    const request = {
      method: 'PUT',
      headers: {
        ...createDefaultHeaders(token),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(
        changeCase.snakeKeys(requestData, { recursive: true })
      )
    }

    dispatch(Creators.getWateringSummaryInit({ deviceId, offset }))

    return Api.call('events/getWateringSummaryByInterval', request)
      .then(response => response.json())
      .then((wateringSummary: DeviceWateringSummary) => {
        const payload = {
          deviceId,
          offset,
          startDate,
          endDate,
          wateringSummary
        }
        return noStoreUpdate
          ? dispatch(Creators.getWateringSummaryCompleteForCSV(payload))
          : dispatch(Creators.getWateringSummaryComplete(payload))
      })
      .catch(err =>
        dispatch(
          Creators.getWateringSummaryComplete({ ...err, deviceId, offset })
        )
      )
  }
}

export const getWateringSummaryForCSV = (options: GetWateringSummaryParams) =>
  getWateringSummary({ ...options, noStoreUpdate: true })

// EVENTS AND ALERTS //////////////////////////////////////////////////////////

/**
 * Get upcoming events
 * @param  {string} userAuthToken - User auth token
 * @param  {string} deviceId - Device ID
 * @param  {number} [hoursAhead=48] - How many hours ahead to request events for
 * @return {promise} - Array of upcoming events
 */
export const getUpcomingEvents = (
  userAuthToken: string,
  deviceId: string,
  hoursAhead: number = 48
) => {
  const Api = new API()
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    const reqData = changeCase.snakeKeys(
      { deviceId, hoursAhead },
      { recursive: true }
    )
    const reqObj = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqData)
    }

    dispatch(Creators.getUpcomingEventsInit(reqData))

    return Api.call('events/upcoming', reqObj)
      .then(response => response.json())
      .then(json => dispatch(Creators.getUpcomingEventsComplete(json)))
      .catch(err => dispatch(Creators.getUpcomingEventsComplete(err)))
  }
}

/**
 * Get next event
 * @param  {string} userAuthToken - User auth token
 * @param  {string} deviceId - Device ID
 * @return {promise} - Next event
 */
export const getNextEvent = (userAuthToken: string, deviceId: string) => {
  const Api = new API()
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    const reqData = changeCase.snakeKeys({ deviceId }, { recursive: true })
    const reqObj = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqData)
    }

    dispatch(Creators.getNextEventInit(reqData))

    return Api.call('events/next', reqObj)
      .then(response => response.json())
      .then(json => dispatch(Creators.getNextEventComplete(json)))
      .catch(err => dispatch(Creators.getNextEventComplete(err)))
  }
}

/**
 * a combination of the device methods getNextEvent and getUpcomingAEvents along with business logic that occurs in multiple
 * components
 * @param {string} token - auth token
 * @param {string} deviceId - device ID
 * @returns {Function} - Dispatch function via Thunk
 */
export const getFutureFeedEvents = (token: string, deviceId: string) => {
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch) {
    return dispatch(getUpcomingEvents(token, deviceId)).then(resp => {
      const { payload: { entries = [] } = {} } = resp
      if (entries.length > 0) {
        return dispatch(
          Creators.updateMeta({ deviceId, data: { futureFeed: entries } })
        )
      } else {
        return (
          dispatch(getNextEvent(token, deviceId))
            .then(resp => {
              const { payload: { entry } = {} } = resp
              return dispatch(
                Creators.updateMeta({
                  deviceId,
                  data: { futureFeed: entry ? [entry] : [] }
                })
              )
            })
            // Make sure we return something
            .catch(() => ({}))
        )
      }
    })
  }
}

/**
 * Get Active Alerts (Alerts that have not been dismissed yet)
 * @param  {string} userAuthToken - User auth token
 * @param  {string} deviceId - Device ID
 * @return {promise} - Array of Alerts
 */
export const getActiveAlerts = (userAuthToken: string, deviceId: string) => {
  const Api = new API()
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch): Promise<*> {
    // Weird formatted obj since server side is a StringList
    const rawReqBody: Object = {
      deviceId: { id: [deviceId] }
    }

    const reqBody = changeCase.snakeKeys(rawReqBody, { recursive: true })
    const error = isInvalid(Schema.GetActiveAlertsRequest, reqBody)
    const reqData = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqBody)
    }

    dispatch(Creators.getActiveAlertsInit(reqBody))

    if (error) {
      return Promise.reject(dispatch(Creators.getActiveAlertsComplete(error)))
    } else {
      // Return API call
      return Api.call('device/getActiveAlerts/', reqData)
        .then(response => response.json())
        .then(json =>
          dispatch(Creators.getActiveAlertsComplete({ deviceId, ...json }))
        )
        .catch(err => dispatch(Creators.getActiveAlertsComplete(err)))
    }
  }
}

/**
 * Dismiss an Alert
 * @param  {string} userAuthToken - User auth token
 * @param  {string} deviceId - Device ID
 * @param  {array} alertIds - alertIds to dismiss
 * @return {promise} - empty
 */
export const dismissAlerts = (
  userAuthToken: string,
  deviceId: string,
  alertIds: Array<string>
) => {
  const Api = new API()
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch): Promise<*> {
    const rawReqBody: Object = {
      deviceId,
      alertIds
    }

    const reqBody = changeCase.snakeKeys(rawReqBody, { recursive: true })
    const error = isInvalid(Schema.DismissAlertsRequest, reqBody)
    const reqData = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqBody)
    }

    dispatch(Creators.dismissAlertsInit())

    if (error) {
      return Promise.reject(dispatch(Creators.dismissAlertsComplete(error)))
    } else {
      // Return API call
      return (
        Api.call('device/dismissAlert/', reqData)
          // A successful call returns empty object {}
          .then(() =>
            dispatch(Creators.dismissAlertsComplete({ deviceId, alertIds }))
          )
          .catch(err => dispatch(Creators.dismissAlertsComplete(err)))
      )
    }
  }
}

// RAIN DELAY //////////////////////////////////////////////////////////////////

/**
 * set rain delay (aka 'watering delay') until specified expiration
 * @param {string} token      user's auth token
 * @param {string} deviceId   ID of device to delay watering
 * @param {Object} expiration a moment object
 * @return {promise} resolving to the dispatch function
 */
export const setRainDelay = (
  token: string,
  deviceId: string,
  expiration: Object
) => {
  const Api = new API()
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch): Promise<*> {
    const rainDelayExpiration = expiration.toISOString()
    const reqBody = changeCase.snakeKeys({ deviceId, rainDelayExpiration })
    const reqData = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(token),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqBody)
    }

    dispatch(Creators.setRainDelayInit(reqData))

    return Api.call('device/setRainDelay', reqData)
      .then(() => dispatch(Creators.setRainDelayComplete(reqData))) // the call doesn't return anything
      .catch(err => dispatch(Creators.setRainDelayComplete(err)))
  }
}

// FEATURE FLAGS //////////////////////////////////////////////////////////////

export const getFeatures = (authToken: string, deviceId: string) => {
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  return function(dispatch: Dispatch): Promise<*> {
    const Api = new API()

    const headers = {
      ...createDefaultHeaders(authToken),
      'Content-Type': 'application/json'
    }

    return Api.call(`device/getDeviceFeatures/${deviceId}`, { headers })
      .then(resp => resp.json())
      .then(json => {
        const features = json.features

        return dispatch(Creators.getFeaturesComplete({ deviceId, features }))
      })
      .catch(err => {
        err.deviceId = deviceId
        dispatch(Creators.getFeaturesComplete(err))
      })
  }
}

type TransferOwnership = (
  token: string,
  {
    deviceId: string,
    grantSharedAccess?: boolean,
    grantSharedAccessExistingUsers: boolean,
    toEmail: string,
    userId: string
  }
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
) => Dispatch => Promise<*>
type GrantControllerOwnershipResponse = {
  grantComplete: boolean
}
/**
 * Transfer device ownership from one user to another
 * @see {@link https://github.com/Rachio/proto/blob/master/user_service.proto#L53}
 * @see {@link https://github.com/Rachio/proto/blob/master/http_api_config.yml#L107}
 * @param {string} token - auth token
 * @param {Object} reqData - device id, email to send transfer email to, and whether shared access should be retained
 * @returns {Object} a function that returns a dispatch promise
 */
export const transferOwnership: TransferOwnership = (token, reqData) => {
  return function(dispatch) {
    const Api: API = new API()
    const reqBody = changeCase.snakeKeys(reqData)
    // $FlowFixMe
    const error: ?ValidationError = isInvalid(
      Schema.TransferOwnershipRequest,
      reqBody
    )

    dispatch(Creators.transferOwnershipInit(reqData))

    if (error) {
      return Promise.reject(dispatch(Creators.transferOwnershipComplete(error)))
    } else {
      return Api.call('user/grantControllerOwnership', {
        method: 'PUT',
        headers: {
          ...createDefaultHeaders(token),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(changeCase.snakeKeys(reqData))
      })
        .then(res => res.json())
        .then(
          (
            grantControllerOwnershipResponse: GrantControllerOwnershipResponse
          ) =>
            dispatch(
              Creators.transferOwnershipComplete(
                grantControllerOwnershipResponse
              )
            )
        )
        .catch(error => dispatch(Creators.transferOwnershipComplete(error)))
    }
  }
}

